import { Link, Outlet, useOutletContext } from "react-router";
import { Button } from "@/components/ui/button";
import { UserIcon } from "@/components/shared/UserIcon";
import { ThemeToggle } from "@/components/theme-toggle";
import { Navigation } from "@/components/public/nav";
import type { Route } from "./+types/_public";
import type { RootOutletContext } from "../root";

export const loader = async ({ context }: Route.LoaderArgs) => {
  const { session, gadgetConfig } = context;

  const userId = session?.get("user");
  const user = userId ? await context.api.user.findOne(userId) : undefined;

  return {
    user,
  };
};

export default function ({ loaderData }: Route.ComponentProps) {
  const { user } = loaderData;

  const context = useOutletContext<RootOutletContext>();

  return (
    <div className="flex flex-col h-full">
      <nav className="border-b shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Navigation />

            <div className="flex items-center space-x-2">
              <ThemeToggle />
              {user ? (
                <Button variant="ghost" size="lg" className="p-2 rounded-full" asChild>
                  <Link to="/signed-in" data-attr="header-signed-in-avatar">
                    Go to app
                    <UserIcon user={user} />
                  </Link>
                </Button>
              ) : (
                <>
                  <Button size="sm" variant="outline" asChild>
                    <Link to="/sign-in" data-attr="header-signin">Sign in</Link>
                  </Button>
                  <Button size="sm" asChild>
                    <Link to="/sign-up" data-attr="header-signup">Get started</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>
      <main className="grow bg-muted/20">
        <Outlet context={context} />
      </main>
      <footer className="border-t shrink-0 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <svg className="h-5 w-5" viewBox="0 0 100 100" fill="none">
                  <path transform="translate(0,-3)" d="M 92 8 L 92 38 C 92 46 86 52 78 52 L 50 52 C 44 52 40 48 40 42 L 40 28 C 40 18 48 8 58 8 Z" fill="#f59e0b"/>
                  <path transform="translate(0,3)" d="M 8 92 L 8 62 C 8 54 14 48 22 48 L 50 48 C 56 48 60 52 60 58 L 60 72 C 60 82 52 92 42 92 Z" fill="#f59e0b"/>
                </svg>
                <span className="font-bold text-sm">Manos</span>
              </div>
              <p className="text-xs text-muted-foreground">Jobs, clients, and invoices for service professionals.</p>
            </div>
            <div>
              <h4 className="font-semibold text-xs uppercase tracking-wide mb-3">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/sign-up" className="hover:text-foreground transition-colors">Get started</Link></li>
                <li><Link to="/sign-in" className="hover:text-foreground transition-colors">Sign in</Link></li>
                {/* HouseProud is a planned sub-brand; the domain isn't ours
                    yet so we render it as a link-styled span with a Coming
                    soon pill -- visual presence without sending visitors to
                    a domain we don't own. Swap to a real anchor when the
                    domain is registered. */}
                <li className="flex items-center gap-2">
                  <span className="text-muted-foreground">HouseProud for homeowners</span>
                  <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 px-1.5 py-0.5 rounded">
                    Coming soon
                  </span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-xs uppercase tracking-wide mb-3">Support</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/about" className="hover:text-foreground transition-colors">About us</Link></li>
                <li><Link to="/contact" className="hover:text-foreground transition-colors">Contact us</Link></li>
                <li><Link to="/help" className="hover:text-foreground transition-colors">Help</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-xs uppercase tracking-wide mb-3">Legal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/privacy" className="hover:text-foreground transition-colors">Privacy policy</Link></li>
                <li><Link to="/terms" className="hover:text-foreground transition-colors">Terms of service</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} Manos. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}